<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { Amount, required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  parent?: any;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  payment_types_id: props.data?.payment_types_id || props.parent?.id || "",
  payment_type: props.data?.payment_type || props.parent?.name || "",
  name: props.data?.name || "",
  multiply: props.data?.multiply || "",
  sort_order: props.data?.sort_order || 0,
  active: props.data?.active || true,
  uid: props.data?.uid || "",
});

setFieldRules("payment_types_id", [required("Currency is required")]);
setFieldRules("name", [required("From Date is required")]);
setFieldRules("sort_order", [
  required("sort_order is required"),
  Amount("sort_order cannot be 0"),
]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") +
        "denomination-" +
        action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
        if (props.parent) {
          fields.value.payment_types_id.value = props.parent.id;
          fields.value.payment_type.value = props.parent.name;
        }
      } else {
        setFormData(response.data);
      }
    }
  }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == "view";
  if (props.mode == "create") {
    resetForm();
  } else if (props.data) {
    setFormData(props.data);
  }
});
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-2 gap-6">
        <div>
          <TextInput
            v-model="fields.payment_type.value"
            label="Payment Type"
            :readonly="true"
            :error="
              fields.payment_types_id.touched
                ? fields.payment_types_id.error
                : null
            "
            @blur="fields.payment_types_id.touched = true"
          />
        </div>

        <TextInput
          v-model="fields.name.value"
          label="Denomination Name"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />

        <NumberInput
          v-model="fields.multiply.value"
          label="Multiply"
          :error="fields.multiply.touched ? fields.multiply.error : null"
          @blur="fields.multiply.touched = true"
        />

        <NumberInput
          v-model="fields.sort_order.value"
          type="number"
          label="Sort Order"
          :min="1"
          :error="fields.sort_order.touched ? fields.sort_order.error : null"
          @blur="fields.sort_order.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
